import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Upload, X, Search, Link2, Music, Loader2, CheckCircle2, Users } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";

// ── Spotify Artist Import Modal ───────────────────────────────────────────────
function SpotifyImportModal({ artistName, onSelect, onClose }) {
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/spotify/search-artist?name=${encodeURIComponent(artistName)}`);
        setResults(res.data || []);
      } catch (e) {
        setError(e.response?.data?.detail || "Spotify-Fehler");
      }
      setLoading(false);
    })();
  }, [artistName]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="spotify-import-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
            <Music size={15} className="text-green-400" />
            Spotify Vorschläge für „{artistName}"
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X size={16} /></button>
        </div>

        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-zinc-400 text-sm">
              <Loader2 size={16} className="animate-spin" /> Suche auf Spotify…
            </div>
          )}
          {error && <p className="text-red-400 text-sm text-center py-6">{error}</p>}
          {!loading && !error && results.length === 0 && (
            <p className="text-zinc-500 text-sm text-center py-6">Keine Ergebnisse gefunden</p>
          )}
          <div className="space-y-2">
            {results.map(r => (
              <button
                key={r.spotify_id}
                onClick={() => onSelect(r)}
                data-testid={`spotify-result-${r.spotify_id}`}
                className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-zinc-800 transition-colors text-left group"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                  {r.image_url
                    ? <img src={r.image_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-zinc-600"><Music size={20} /></div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{r.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-zinc-400 text-xs flex items-center gap-1">
                      <Users size={11} /> {r.followers?.toLocaleString("de-DE")} Follower
                    </span>
                    {r.popularity > 0 && (
                      <span className="text-zinc-500 text-xs">Score: {r.popularity}/100</span>
                    )}
                  </div>
                  {r.genres?.length > 0 && (
                    <p className="text-zinc-600 text-xs truncate mt-0.5">{r.genres.join(", ")}</p>
                  )}
                </div>
                <span className="text-xs text-green-400 font-semibold opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  Verwenden
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Searchable alias picker – saves immediately and bidirectionally */
function AliasSection({ artistId }) {
  const [aliases, setAliases]     = useState([]);
  const [query, setQuery]         = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // Load current aliases
  const loadAliases = useCallback(async () => {
    const res = await api.get(`/artists/${artistId}`);
    setAliases(res.data.aliases || []);
  }, [artistId]);

  useEffect(() => { loadAliases(); }, [loadAliases]);

  // Debounced artist search
  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/artists/?search=${encodeURIComponent(query)}&limit=8`);
        const items = res.data.items || [];
        // Exclude self and already-linked aliases
        const linkedIds = new Set(aliases.map(a => a.id));
        setSuggestions(items.filter(a => a.id !== artistId && !linkedIds.has(a.id)));
      } catch {}
      finally { setSearching(false); }
    }, 300);
  }, [query, aliases, artistId]);

  const handleAdd = async (target) => {
    try {
      await api.post(`/artists/${artistId}/aliases/${target.id}`);
      setQuery("");
      setSuggestions([]);
      loadAliases();
    } catch (e) { alert(e.response?.data?.detail || "Fehler"); }
  };

  const handleRemove = async (aliasId) => {
    if (!window.confirm("Verbindung entfernen?")) return;
    try {
      await api.delete(`/artists/${artistId}/aliases/${aliasId}`);
      loadAliases();
    } catch {}
  };

  return (
    <div className="space-y-3">
      {/* Current aliases */}
      {aliases.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {aliases.map(a => (
            <div key={a.id} className="flex items-center gap-2 pl-2 pr-1 py-1 bg-zinc-800 rounded-full border border-white/8 text-sm">
              {a.bild_url
                ? <img src={a.bild_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                : <div className="w-5 h-5 rounded-full bg-rose-900/50 flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-rose-300">{a.name[0]}</div>
              }
              <span className="text-zinc-200 text-xs font-medium">{a.name}</span>
              <button onClick={() => handleRemove(a.id)}
                className="text-zinc-600 hover:text-red-400 transition-colors ml-0.5">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Search input */}
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Artist suchen und verknüpfen…"
          className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600"
        />
        {/* Dropdown */}
        {suggestions.length > 0 && (
          <div className="absolute top-full mt-1 left-0 right-0 bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden z-20 shadow-xl">
            {suggestions.map(s => (
              <button key={s.id} type="button" onClick={() => handleAdd(s)}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-zinc-800 transition-colors text-left">
                {s.bild_url
                  ? <img src={s.bild_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                  : <div className="w-7 h-7 rounded-full bg-rose-900/40 flex items-center justify-center flex-shrink-0 text-xs font-bold text-rose-300">{s.name[0]}</div>
                }
                <span className="text-white text-sm">{s.name}</span>
                {s.release_count > 0 && <span className="text-zinc-600 text-xs ml-auto">{s.release_count} Releases</span>}
              </button>
            ))}
          </div>
        )}
        {searching && query.length >= 2 && !suggestions.length && (
          <div className="absolute top-full mt-1 left-0 right-0 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-500 text-xs z-20">Suche…</div>
        )}
      </div>
    </div>
  );
}

export default function ArtistFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: "", slug: "", biografie: "", geburtstag: "", wohnort: "", bild_url: "",
    social_links: { facebook: "", instagram: "", soundcloud: "", beatport: "", youtube: "", spotify: "", twitch: "" },
  });
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState("");
  const [spotifyModal, setSpotifyModal] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      api.get(`/artists/${id}`)
        .then((res) => {
          const a = res.data;
          setForm({
            name: a.name || "", slug: a.slug || "", biografie: a.biografie || "",
            geburtstag: a.geburtstag || "", wohnort: a.wohnort || "", bild_url: a.bild_url || "",
            social_links: { facebook: "", instagram: "", soundcloud: "", beatport: "", youtube: "", spotify: "", twitch: "", ...(a.social_links || {}) },
          });
          setSlugManuallyEdited(true); // Slug beim Bearbeiten nicht auto-überschreiben
        })
        .catch(() => navigate("/admin/artists"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate]);

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const set = (field) => (e) => {
    const val = e.target.value;
    setForm((f) => {
      const updated = { ...f, [field]: val };
      // Slug auto-aktualisieren wenn Name geändert und Slug nicht manuell überschrieben
      if (field === "name" && !slugManuallyEdited) {
        updated.slug = val.toLowerCase()
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      }
      return updated;
    });
  };
  const setSocial = (field) => (e) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [field]: e.target.value } }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, bild_url: res.data.url }));
    } catch { alert("Upload fehlgeschlagen"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (isEdit) await api.put(`/artists/${id}`, form);
      else await api.post("/artists/", form);
      navigate("/admin/artists");
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";
  const labelCls = "block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1.5";

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin/artists" className="text-zinc-500 hover:text-white text-sm">Artists</Link>
          <span className="text-zinc-700">/</span>
          <h1 className="text-xl font-black text-white font-heading">{isEdit ? "Artist bearbeiten" : "Neuer Artist"}</h1>
        </div>
        {error && <div className="bg-red-900/20 border border-red-800/40 text-red-400 text-sm px-3 py-2 rounded-md mb-4">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input value={form.name} onChange={set("name")} required className={inputCls} placeholder="Künstlername" />
            </div>
            <div>
              <label className={labelCls}>Slug</label>
              <input
                value={form.slug}
                onChange={(e) => { setSlugManuallyEdited(true); set("slug")(e); }}
                className={inputCls}
                placeholder="auto-generiert"
              />
            </div>
            <div>
              <label className={labelCls}>Geburtstag</label>
              <input type="date" value={form.geburtstag} onChange={set("geburtstag")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Wohnort</label>
              <input value={form.wohnort} onChange={set("wohnort")} className={inputCls} placeholder="Stadt, Land" />
            </div>
          </div>

          {/* Bild */}
          <div>
            <label className={labelCls}>Profilbild</label>
            <div className="flex gap-3">
              {form.bild_url && (
                <div className="w-16 h-16 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0">
                  <img src={form.bild_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input value={form.bild_url} onChange={set("bild_url")} className={inputCls} placeholder="https://... oder Datei hochladen" />
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-md transition-colors">
                    <Upload size={13} /> Hochladen
                  </button>
                  {isEdit && (
                    <button
                      type="button"
                      onClick={() => setSpotifyModal(true)}
                      data-testid="spotify-import-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs rounded-md transition-colors"
                    >
                      <Music size={13} /> Bild von Spotify laden
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </div>
            </div>
          </div>

          {/* Biografie */}
          <div>
            <label className={labelCls}>Biografie</label>
            <textarea value={form.biografie} onChange={set("biografie")} rows={5}
              className={`${inputCls} resize-y min-h-[120px]`} placeholder="Über den Artist..." />
          </div>

          {/* Social Links */}
          <div>
            <label className={labelCls}>Social Links</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["facebook", "instagram", "soundcloud", "beatport", "youtube", "spotify", "twitch"].map((s) => (
                <div key={s}>
                  <label className="text-zinc-600 text-xs capitalize mb-1 block">{s}</label>
                  <input value={form.social_links[s] || ""} onChange={setSocial(s)} className={inputCls} placeholder={`https://${s}.com/...`} />
                </div>
              ))}
            </div>
          </div>

          {/* Aliases – nur bei bestehenden Artists */}
          {isEdit && (
            <div>
              <label className={labelCls}><Link2 size={12} className="inline mr-1" />Pseudonyme & andere Projekte</label>
              <p className="text-zinc-600 text-xs mb-2">Verknüpfte Artists werden auf beiden Profilseiten angezeigt. Die Verbindung wird automatisch bidirektional gespeichert.</p>
              <AliasSection artistId={id} />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-md transition-colors">
              {saving ? "Speichern..." : "Speichern"}
            </button>
            <Link to="/admin/artists" className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-md transition-colors">Abbrechen</Link>
          </div>
        </form>
      </div>
      </div>

      {/* Spotify Import Modal */}
      {spotifyModal && (
        <SpotifyImportModal
          artistName={form.name}
          onClose={() => setSpotifyModal(false)}
          onSelect={(result) => {
            setForm(f => ({
              ...f,
              bild_url: result.image_url || f.bild_url,
              social_links: {
                ...f.social_links,
                spotify: result.spotify_url || f.social_links.spotify,
              },
            }));
            setSpotifyModal(false);
          }}
        />
      )}
    </AdminLayout>
  );
}
